import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { Box } from "@mui/system";
import { CircularProgress, Step, StepButton, Stepper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ProcessInfo from "./UpdateBrandDataSteps/ProcessInfo";
import ProcessSetting from "./UpdateBrandDataSteps/ProcessSetting";
import ReviewAndRun from "./UpdateBrandDataSteps/Review&Run";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useSelector } from "react-redux";
import {
  setEndDate,
  setIsLoading,
  setIsSuccess,
  setModalOpen,
  setSelectedAction,
  setSelectedActionId,
  setSelectedBrandConfigs,
  setSelectedUnifierDestinationTableId,
  setStartDate,
} from "../../../../features/dataCollectionBrandsSlice";
import { useDispatch } from "react-redux";
import { selectUserToken } from "../../../../features/userSlice";
import { dataCollectionBrandsEndPointLogic } from "../../../../logic/DataCollection/Brands/BrandsEndPointLogic";
import dayjs from "dayjs";
import { actionIdMapping } from "./actionIds";
interface Props {
  open: boolean;
}

export default function UpdateBrandData({ open }: Props) {
  const {
    selectedAction,
    selectedBrandConfigs,
    selectedActionId,
    brandDataInModal,
    startDate,
    endDate,
    isLoading,
    selectedUnifierDestinationTableId,
  } = useSelector((state: any) => state.dataCollectionBrands);
  const oauthToken: any = useSelector(selectUserToken);
  const dispatch = useDispatch();
  const steps = ["Process Info", "Process Settings", "Review & Run"];
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>(
    {}
  );
  const hasError = false; // Define hasError variable Plese use usestate here when develop
  const handleClose = () => {
    dispatch(setModalOpen({ isOpen: false, data: {} }));
    dispatch(setSelectedAction(null));
    dispatch(setSelectedActionId(null));
    setActiveStep(0);
    setCompleted({});
    dispatch(setSelectedBrandConfigs([]));
    dispatch(setSelectedUnifierDestinationTableId([]));
    dispatch(setStartDate(dayjs().format("YYYY-MM-DD")));
    dispatch(setEndDate(dayjs().format("YYYY-MM-DD")));
  };

  const handleNext = () => {
    // Always mark current step as completed
    setCompleted((prev) => ({ ...prev, [activeStep]: true }));

    if (selectedAction === "End_to_end_sync") {
      setActiveStep(2); // Skip to Review & Run
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };
  const isNextDisabled =
    activeStep === steps.indexOf("Process Settings") &&
    ((selectedAction === "Fetcher" && selectedBrandConfigs.length === 0) ||
      (selectedAction === "Unifier" &&
        selectedUnifierDestinationTableId?.length === 0));

  const handleActionSelect = (action: string) => {
    dispatch(setSelectedAction(action));

    const env =
      import.meta.env.VITE_ENV === "production" ? "production" : "staging";
    const id = actionIdMapping[env][action];

    dispatch(setSelectedActionId(id));
  };

  const handleBack = () => {
    if (activeStep === 2 && selectedAction === "End_to_end_sync") {
      // If coming from skipped step, go back to step 0
      setActiveStep(0);
      setCompleted((prev) => {
        const newCompleted = { ...prev };
        delete newCompleted[0]; // Unmark step 0 completion
        return newCompleted;
      });
    } else {
      setActiveStep((prev) => prev - 1);
      setCompleted((prev) => {
        const newCompleted = { ...prev };
        delete newCompleted[activeStep - 1]; // Unmark previous step
        return newCompleted;
      });
    }
  };

  const handleStep = (step: number) => {
    if (selectedAction) {
      setActiveStep(step);
    }
  };

  const handleFinish = async () => {
    try {
      dispatch(setIsLoading(true));
      let payload: any = {
        process_id: selectedActionId,
        start_date: startDate,
        end_date: endDate,
      };

      if (selectedActionId === 196 || selectedActionId === 100) {
        // End-to-End Sync: use brandId
        const brandIds = brandDataInModal?.map((b: any) => b.brandId);
        payload = { ...payload, brand_id: brandIds };
      } else if (selectedActionId === 190 || selectedActionId === 97) {
        // Fetcher: use tableId
        const tableIds = selectedBrandConfigs?.flatMap((brand: any) =>
          brand.selectedConfigs?.map((cfg: any) => cfg.tableId)
        );
        payload = { ...payload, table_id: tableIds };
      } else if (selectedActionId === 177 || selectedActionId === 90) {
        // Unifier: use destinationTableId
        const destinationTableIds = selectedUnifierDestinationTableId?.map(
          (cfg: any) => cfg.destinationTableId
        );
        const brandIds = brandDataInModal?.map((b: any) => b.brandId);
        payload = {
          ...payload,
          brand_id: brandIds,
          destination_table_id: destinationTableIds,
        };
      }

      await dataCollectionBrandsEndPointLogic.brandsRunProcess(
        oauthToken,
        payload
      );
    } catch (err) {
      console.error("Error running brand process:", err);
      dispatch(setIsSuccess(false));
    } finally {
      dispatch(setIsLoading(false));
      handleClose();
      setTimeout(() => {
        setActiveStep(0);
        setCompleted({});
        dispatch(setSelectedAction(null));
      }, 300);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Box>
        <DialogTitle id="alert-dialog-title">{"Update Brand Data"}</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <Stepper nonLinear activeStep={activeStep}>
              {steps.map((label, index) => (
                <Step key={label} completed={completed[index]}>
                  <StepButton
                    onClick={() => {
                      if (completed[index]) handleStep(index);
                    }}
                    disabled={
                      (index === 1 && selectedAction === "End_to_end_sync") ||
                      !completed[index]
                    }
                    sx={{
                      "& .MuiStepIcon-root": {
                        color:
                          label === "Save" && hasError
                            ? "rgba(211, 47, 47, 1) !important" // Red if there's an error
                            : activeStep === index || completed[index]
                            ? "rgba(156, 39, 176, 1) !important"
                            : "rgba(0, 0, 0, 0.38) !important",
                      },
                      "&.Mui-active .MuiStepIcon-root, &.Mui-completed .MuiStepIcon-root":
                        {
                          color:
                            label === "Save" && hasError
                              ? "rgba(211, 47, 47, 1) !important" // Keep red if error exists
                              : "rgba(156, 39, 176, 1) !important",
                        },
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Conditional rendering based on active step */}
          {activeStep === 0 && (
            <Box>
              <ProcessInfo onActionSelect={handleActionSelect} />
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <ProcessSetting />
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <>
                <ReviewAndRun />
              </>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "0px 24px 20px 24px" }}>
          <Button
            onClick={handleClose} // Function to close the dialog
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBack}
            variant="outlined"
            color="secondary"
            sx={activeStep === 0 ? { display: "none" } : {}}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Button
            onClick={
              activeStep === steps.length - 1 ? handleFinish : handleNext
            }
            color="secondary"
            data-tracker="360"
            variant="contained"
            disabled={!selectedAction || isLoading || isNextDisabled}
            endIcon={
              isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : activeStep === steps.length - 1 ? (
                <PlayCircleIcon />
              ) : (
                <ArrowForwardOutlinedIcon />
              )
            }
          >
            {isLoading
              ? "Running Process About to Start..."
              : activeStep === steps.length - 1
              ? "Run Process"
              : "Next"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
